import React, { FC } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faBriefcase } from "@fortawesome/pro-regular-svg-icons/faBriefcase";
import { faHome } from "@fortawesome/pro-regular-svg-icons/faHome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form from "antd/es/form";
import Radio from "antd/es/radio";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import TripType from "@mapmycustomers/shared/enum/mileageTracking/TripType";
import Trip from "@mapmycustomers/shared/types/entity/Trip";
import { AvatarWithName } from "@mapmycustomers/ui";

import Chip from "@app/component/Chip";
import CurrencyInput from "@app/component/input/CurrencyInput";
import RadioGroup from "@app/component/input/RadioGroup";
import TextArea from "@app/component/input/TextArea";
import UserPreviewCard from "@app/component/UserPreviewCard";
import { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import { getTripTypeDisplayName } from "@app/util/ui";

import styles from "./FormFields.module.scss";

const messages = defineMessages({
  classification: {
    id: "reports.mileageTracking.preview.form.fields.classification",
    defaultMessage: "Classification",
    description: "Classification field on the Trip preview pane",
  },
  notes: {
    id: "reports.mileageTracking.preview.form.fields.notes",
    defaultMessage: "Notes",
    description: "Notes field on the Trip preview pane",
  },
  parking: {
    id: "reports.mileageTracking.preview.form.fields.parking",
    defaultMessage: "Parking",
    description: "Parking field on the Trip preview pane",
  },
  tolls: {
    id: "reports.mileageTracking.preview.form.fields.tolls",
    defaultMessage: "Tolls",
    description: "Tolls field on the Trip preview pane",
  },
  user: {
    id: "reports.mileageTracking.preview.form.fields.user",
    defaultMessage: "User",
    description: "User field on the Trip preview pane",
  },
});

const typeToIcon: Record<"unclassified" | TripType, IconDefinition> = {
  [TripType.BUSINESS]: faBriefcase,
  [TripType.PERSONAL]: faHome,
  unclassified: faInfoCircle,
};

export interface FormFieldsProps {
  disabled?: boolean;
  trip?: Trip;
}

const FormFields: FC<FormFieldsProps> = ({ disabled, trip }) => {
  const intl = useIntl();

  return (
    <>
      {disabled && trip && (
        <Form.Item label={intl.formatMessage(messages.user)} name={TripFieldName.USER}>
          <UserPreviewCard userId={trip.user.id}>
            <div>
              <Chip inline>
                <AvatarWithName
                  avatarClassName={styles.avatar}
                  className={styles.avatarContainer}
                  user={trip.user}
                />
              </Chip>
            </div>
          </UserPreviewCard>
        </Form.Item>
      )}
      <Form.Item label={intl.formatMessage(messages.classification)} name={TripFieldName.TYPE}>
        {disabled ? (
          <Chip className={styles.typeTag} inline>
            <FontAwesomeIcon
              className={cn(styles.typeIcon, {
                [styles.business]: trip?.type === TripType.BUSINESS,
                [styles.personal]: trip?.type === TripType.PERSONAL,
              })}
              icon={typeToIcon[trip?.type ?? "unclassified"]}
            />
            <span>{getTripTypeDisplayName(intl, trip?.type ?? null)}</span>
          </Chip>
        ) : (
          <RadioGroup<TripType> disabled={disabled}>
            {Object.values(TripType).map((type) => (
              <Radio.Button key={type} value={type}>
                <div className={styles.typeTag}>
                  <FontAwesomeIcon
                    className={cn(styles.typeIcon, {
                      [styles.business]: type === TripType.BUSINESS,
                      [styles.personal]: type === TripType.PERSONAL,
                    })}
                    icon={typeToIcon[type]}
                  />
                  <span>{getTripTypeDisplayName(intl, type)}</span>
                </div>
              </Radio.Button>
            ))}
          </RadioGroup>
        )}
      </Form.Item>
      <Form.Item label={intl.formatMessage(messages.parking)} name={TripFieldName.PARKING_FEE}>
        <CurrencyInput
          disabled={disabled}
          // inputClassName={styles.rateField}
          // We're ok losing some precision here
          // eslint-disable-next-line no-loss-of-precision
          max={99999999999999.99}
          min={0}
          size="middle"
          step={0.1}
        />
      </Form.Item>
      <Form.Item label={intl.formatMessage(messages.tolls)} name={TripFieldName.TOLL_FEE}>
        <CurrencyInput
          disabled={disabled}
          // inputClassName={styles.rateField}
          // We're ok losing some precision here
          // eslint-disable-next-line no-loss-of-precision
          max={99999999999999.99}
          min={0}
          size="middle"
          step={0.1}
        />
      </Form.Item>
      <Form.Item label={intl.formatMessage(messages.notes)} name={TripFieldName.NOTE}>
        <TextArea disabled={disabled} />
      </Form.Item>
    </>
  );
};

export default FormFields;
